import React, { useState, useRef, useEffect } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";

const LBL = {
  pt: { aria: "Selecionar idioma", tip: "Idioma", sub: "Define o idioma do site", menu: "Idioma", menuSub: "Escolha como deseja visualizar o MY CHRISTMAS." },
  "pt-PT": { aria: "Selecionar idioma", tip: "Idioma", sub: "Define o idioma do site", menu: "Idioma", menuSub: "Escolha como pretende ver o MY CHRISTMAS." },
  en: { aria: "Select language", tip: "Language", sub: "Sets the site language", menu: "Language", menuSub: "Choose how you want to view MY CHRISTMAS." },
  es: { aria: "Seleccionar idioma", tip: "Idioma", sub: "Define el idioma del sitio", menu: "Idioma", menuSub: "Elige cómo quieres ver MY CHRISTMAS." },
  fr: { aria: "Sélectionner la langue", tip: "Langue", sub: "Définit la langue du site", menu: "Langue", menuSub: "Choisissez comment afficher MY CHRISTMAS." },
  it: { aria: "Seleziona lingua", tip: "Lingua", sub: "Imposta la lingua del sito", menu: "Lingua", menuSub: "Scegli come visualizzare MY CHRISTMAS." },
};

export default function LanguageSelector({ compact = false, onChange }) {
  const { lang, setLang, languages } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const L = LBL[lang] || LBL.pt;

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const current = languages.find((l) => l.code === lang) || languages[0];

  return (
    <div className="group relative" ref={ref}>
      <button
        data-testid="language-selector-button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox" aria-expanded={open} aria-label={L.aria} title={L.tip}
        className={`flex items-center gap-2 rounded-full border border-xmas-gold/25 bg-xmas-navycard/70 text-xmas-snow transition hover:border-xmas-gold/50 ${compact ? "px-2 py-1.5 text-sm sm:px-3" : "px-4 py-2"}`}
      >
        <Globe className="h-4 w-4 text-xmas-gold" />
        <span className={`font-medium ${compact ? "hidden sm:inline" : ""}`}>{current.label}</span>

        <ChevronDown className={`h-3.5 w-3.5 text-xmas-slate transition ${open ? "rotate-180" : ""}`} />
      </button>
      {!open && (
        <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg border border-xmas-gold/25 bg-xmas-navy px-3 py-1.5 text-center opacity-0 shadow-xl transition-opacity duration-200 group-hover:opacity-100 sm:block" data-testid="language-tooltip">
          <p className="text-xs font-semibold text-xmas-gold">{L.tip}</p>
          <p className="text-[10px] text-xmas-slate">{L.sub}</p>
        </div>
      )}
      {open && (
        <ul role="listbox" data-testid="language-menu" className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-xl border border-xmas-gold/25 bg-xmas-navycard shadow-2xl">
          <li className="border-b border-xmas-gold/10 px-4 py-2.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-xmas-gold">{L.menu}</p>
            <p className="mt-0.5 text-[11px] text-xmas-slate">{L.menuSub}</p>
          </li>
          {languages.map((l) => {
            const active = l.code === lang;
            return (
              <li key={l.code}>
                <button data-testid={`language-option-${l.code}`} onClick={() => { setLang(l.code); localStorage.setItem("mc_lang_manual", "1"); onChange?.(l.code); api.post("/analytics/event", { event: "language_selected", meta: { language: l.code, manual: true } }).catch(() => {}); setOpen(false); }}
                  className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition ${active ? "bg-xmas-gold/15 text-xmas-gold" : "text-xmas-snow hover:bg-white/5"}`}>
                  {l.label}
                  {active && <Check className="h-4 w-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
